import ReactApexChart from 'react-apexcharts';
import { useState, useEffect } from 'react';
import { Button, Card, CardBody, CardHeader, CardTitle, Col, Row } from 'react-bootstrap';
import IconifyIcon from '../../../components/IconifyIcon';
import { Link } from 'react-router-dom';
import MoroccoOrdersMap from './MoroccoOrdersMap';
import apiService from '../../../services/api.service';

const Conversions = () => {
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stockStats, setStockStats] = useState({
    total_stock: 0,
    max_capacity: 0,
    stock_percentage: 0,
    total_sold: 0,
    low_stock_count: 0,
    total_products: 0
  });
  const [stockLoading, setStockLoading] = useState(true);

  useEffect(() => {
    const fetchTopProducts = async () => {
      try {
        const response = await apiService.get('/dashboard/top-products');
        if (response.success) {
          setTopProducts(response.data);
        }
      } catch (error) {
        console.error('Error fetching top products:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchStockStats = async () => {
      try {
        const response = await apiService.get('/dashboard/stock-stats');
        if (response.success) {
          setStockStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching stock stats:', error);
      } finally {
        setStockLoading(false);
      }
    };

    fetchTopProducts();
    fetchStockStats();
  }, []);

  // Dynamic chart options based on stock stats
  const getChartOptions = () => {
    const stockColor = stockStats.stock_percentage > 50 ? '#22c55e' : 
                       stockStats.stock_percentage > 25 ? '#f59e0b' : '#ef4444';
    
    return {
      chart: {
        height: 292,
        type: 'radialBar'
      },
      plotOptions: {
        radialBar: {
          startAngle: -135,
          endAngle: 135,
          dataLabels: {
            name: {
              fontSize: '14px',
              color: '#6b7280',
              offsetY: 100
            },
            value: {
              offsetY: 55,
              fontSize: '24px',
              fontWeight: 'bold',
              color: stockColor,
              formatter: function (val) {
                return val + '%';
              }
            }
          },
          track: {
            background: 'rgba(170,184,197, 0.2)',
            margin: 0
          }
        }
      },
      fill: {
        type: 'solid',
        colors: [stockColor]
      },
      stroke: {
        dashArray: 4
      },
      colors: [stockColor],
      labels: ['Niveau Stock'],
      responsive: [
        {
          breakpoint: 380,
          options: {
            chart: {
              height: 180
            }
          }
        }
      ],
      grid: {
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0
        }
      }
    };
  };

  return (
    <>
      <Col lg={4} className="mt-3 mb-4">
        <Card>
          <CardBody>
            <CardTitle as={'h5'}>État du Stock</CardTitle>
            {stockLoading ? (
              <div className="text-center py-5">
                <span className="spinner-border spinner-border-sm" role="status"></span>
              </div>
            ) : (
              <>
                <ReactApexChart
                  options={getChartOptions()}
                  series={[stockStats.stock_percentage]}
                  height={292}
                  type="radialBar"
                  className="apex-charts mb-2 mt-n2"
                />
                <Row className="text-center">
                  <Col xs={6}>
                    <p className="text-muted mb-2">Stock Actuel</p>
                    <h3 className="text-dark mb-3">{stockStats.total_stock.toLocaleString()}</h3>
                  </Col>
                  <Col xs={6}>
                    <p className="text-muted mb-2">Capacité Max</p>
                    <h3 className="text-dark mb-3">{stockStats.max_capacity.toLocaleString()}</h3>
                  </Col>
                </Row>
                <Row className="text-center mt-2">
                  <Col xs={6}>
                    <p className="text-muted mb-1">Vendus</p>
                    <span className="badge badge-soft-success">{stockStats.total_sold.toLocaleString()}</span>
                  </Col>
                  <Col xs={6}>
                    <p className="text-muted mb-1">Stock Faible</p>
                    <span className={`badge badge-soft-${stockStats.low_stock_count > 0 ? 'danger' : 'success'}`}>
                      {stockStats.low_stock_count} produits
                    </span>
                  </Col>
                </Row>
              </>
            )}
          </CardBody>
        </Card>
      </Col>
      <Col lg={4} className="mt-3 mb-4">
        <Card>
          <CardBody>
            <CardTitle as={'h5'}>Commandes par Ville</CardTitle>
            <MoroccoOrdersMap />
          </CardBody>
        </Card>
      </Col>
      <Col lg={4} className="mt-3 mb-4">
        <Card className="card-height-100">
          <CardHeader className="d-flex align-items-center justify-content-between gap-2 border-bottom">
            <CardTitle as={'h4'} className="mb-0 flex-grow-1">
              Top Produits
            </CardTitle>
            <Link to="/admin/products">
              <Button variant="link" size="sm" className="text-muted p-0 text-decoration-none fw-semibold">
                Voir Tout
              </Button>
            </Link>
          </CardHeader>
          <div className="table-responsive" style={{ padding: '1.25rem' }}>
            <table className="table table-hover table-nowrap table-centered m-0">
              <thead className="bg-light bg-opacity-50">
                <tr>
                  <th className="text-muted ps-3 fw-semibold" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nom Produit</th>
                  <th className="text-muted fw-semibold text-center" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ventes</th>
                  <th className="text-muted fw-semibold text-center" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tendance</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="3" className="text-center py-3">
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Chargement...
                    </td>
                  </tr>
                ) : topProducts.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center py-3 text-muted">
                      Aucun produit trouvé
                    </td>
                  </tr>
                ) : (
                  topProducts.map((product, idx) => (
                    <tr key={product.id || idx}>
                      <td className="ps-3">
                        <Link to={`/admin/products/${product.id}`} className="text-dark text-decoration-none">
                          {product.name}
                        </Link>
                      </td>
                      <td className="text-dark text-center">{product.sold_count}</td>
                      <td className="text-center">
                        <span className={`badge badge-soft-${
                          product.trend_direction === 'up' ? 'success' : 'danger'
                        } fw-semibold d-inline-flex align-items-center gap-1`}>
                          {product.trend_direction === 'up' && (
                            <IconifyIcon icon="bx:up-arrow-alt" style={{ fontSize: '14px' }} />
                          )}
                          {product.trend_direction === 'down' && (
                            <IconifyIcon icon="bx:down-arrow-alt" style={{ fontSize: '14px' }} />
                          )}
                          {product.trend_percentage || 0}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </Col>
    </>
  );
};

export default Conversions;
